/**
 * Site boundary (geofence) for a job — stored in jobs.site_boundary as a
 * JSON array of vertices. Used as a map overlay (live map + report site map)
 * and to widen the report map's framing extent.
 */

export interface SiteBoundaryPoint {
  latitude: number
  longitude: number
}

/** Minimum vertices for a valid boundary polygon */
export const MIN_BOUNDARY_POINTS = 3

function isValidPoint(value: unknown): value is SiteBoundaryPoint {
  if (typeof value !== 'object' || value === null) return false
  const p = value as Record<string, unknown>
  return (
    typeof p.latitude === 'number' &&
    Number.isFinite(p.latitude) &&
    Math.abs(p.latitude) <= 90 &&
    typeof p.longitude === 'number' &&
    Number.isFinite(p.longitude) &&
    Math.abs(p.longitude) <= 180
  )
}

/**
 * Parses a stored jobs.site_boundary value. Returns the vertex list when it
 * is a valid polygon (>= 3 valid points), otherwise null.
 */
export function parseSiteBoundary(value: unknown): SiteBoundaryPoint[] | null {
  if (!Array.isArray(value)) return null
  if (value.length < MIN_BOUNDARY_POINTS) return null
  if (!value.every(isValidPoint)) return null
  return value.map((p) => ({ latitude: p.latitude, longitude: p.longitude }))
}
